// ExternalChecklistLogic.js
import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import CryptoJS from "crypto-js";  // Import crypto-js

export default {
  name: "ExternalChecklistLogic",
  props: {
    /**
     * The checklist ID from the route, e.g. /external-checklist/:checkListId
     * If not provided, it tries to read from this.$route.params.checkListId
     */
    initialCheckListId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // Top-level fields in /projectsExtrChecklist/{checkListID}
      producerName: "",
      sentBy: "",
      projectName: "",
      timeSent: null,

      // Sub-collection fields: /projectsExtrChecklist/{checkListID}/checkList/creative
      creative: {
        copy: "No",
        creativeAssets: "No",
        creativePDF: "No",
        deliverablesList: "No",
        preProdQC: "No"
      },

      // Sub-collection fields: /projectsExtrChecklist/{checkListID}/checkList/teamworkCard
      teamworkCard: {
        briefInDescription: "No",
        deadline: "No",
        twAssigned: "No"
      },

      localCheckListId: null,
      loading: false,
      error: null,
      successMessage: "",
      
      // New fields for password protection:
      passwordHash: "", // stored hashed password from Firestore
      verified: false,  // whether user has entered the correct password
      inputPassword: "",// password entered by user
      passError: ""
    };
  },
  async created() {
    // Use the prop or the route param
    this.localCheckListId =
      this.initialCheckListId || this.$route?.params?.checkListId;
    if (!this.localCheckListId) {
      this.error = "No checkListId provided.";
      return;
    }
    // Fetch checklist data (including the stored hashed password)
    await this.fetchCheckListData();
  },
  methods: {
    /**
     * Fetch the top-level document and sub-documents from Firestore.
     */
    async fetchCheckListData() {
      this.loading = true;
      this.error = null;
      try {
        // Top-level document
        const checklistRef = doc(db, "projectsExtrChecklist", this.localCheckListId);
        const checklistSnap = await getDoc(checklistRef);
        if (checklistSnap.exists()) {
          const data = checklistSnap.data();
          this.producerName = data.producerName || "";
          this.sentBy = data.sentBy || "";
          this.projectName = data.projectName || "";
          this.timeSent = data.timeSent || null;
          if (data.password) {
            this.passwordHash = data.password;
          }
        } else {
          // If it doesn't exist, create it so sub-collections can be stored
          await setDoc(checklistRef, {
            producerName: "",
            sentBy: "",
            projectName: "",
            timeSent: serverTimestamp()
          });
          this.timeSent = new Date(); // local fallback
        }

        // Sub-collection: /checkList/creative
        const creativeRef = doc(checklistRef, "checkList", "creative");
        const creativeSnap = await getDoc(creativeRef);
        if (creativeSnap.exists()) {
          this.creative = {
            ...this.creative, // keep default structure
            ...creativeSnap.data()
          };
        } else {
          // Create a default doc with "No"
          await setDoc(creativeRef, this.creative);
        }

        // Sub-collection: /checkList/teamworkCard
        const teamworkRef = doc(checklistRef, "checkList", "teamworkCard");
        const teamworkSnap = await getDoc(teamworkRef);
        if (teamworkSnap.exists()) {
          this.teamworkCard = {
            ...this.teamworkCard,
            ...teamworkSnap.data()
          };
        } else {
          // Create a default doc with "No"
          await setDoc(teamworkRef, this.teamworkCard);
        }
      } catch (err) {
        this.error = "Error fetching checklist data.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update top-level document fields.
     */
    async saveTopLevel() {
      try {
        const checklistRef = doc(db, "projectsExtrChecklist", this.localCheckListId);
        await updateDoc(checklistRef, {
          producerName: this.producerName,
          sentBy: this.sentBy,
          projectName: this.projectName
        });
        this.successMessage = "Top-level info updated.";
        setTimeout(() => (this.successMessage = ""), 2000);
      } catch (err) {
        this.error = "Error saving top-level info.";
        console.error(err);
      }
    },

    /**
     * Update the "creative" sub-document.
     */
    async saveCreative() {
      try {
        const creativeRef = doc(
          db,
          "projectsExtrChecklist",
          this.localCheckListId,
          "checkList",
          "creative"
        );
        await updateDoc(creativeRef, this.creative);
        this.successMessage = "Creative checklist updated.";
        setTimeout(() => (this.successMessage = ""), 2000);
      } catch (err) {
        this.error = "Error saving creative checklist.";
        console.error(err);
      }
    },

    /**
     * Update the "teamworkCard" sub-document.
     */
    async saveTeamworkCard() {
      try {
        const teamworkRef = doc(
          db,
          "projectsExtrChecklist",
          this.localCheckListId,
          "checkList",
          "teamworkCard"
        );
        await updateDoc(teamworkRef, this.teamworkCard);
        this.successMessage = "Teamwork Card updated.";
        setTimeout(() => (this.successMessage = ""), 2000);
      } catch (err) {
        this.error = "Error saving teamwork card.";
        console.error(err);
      }
    },

    /**
     * Hash a given password using crypto-js (SHA-256).
     */
    async hashPassword(password) {
      // Using crypto-js instead of crypto.subtle.digest
      return CryptoJS.SHA256(password).toString();
    },

    /**
     * Verify the user-entered password by comparing its hash with the stored hash.
     */
    async verifyPassword() {
      this.passError = "";
      if (!this.inputPassword) {
        this.passError = "Please enter a password.";
        return;
      }
      try {
        const hashedInput = await this.hashPassword(this.inputPassword);
        if (hashedInput === this.passwordHash) {
          this.verified = true;
        } else {
          this.passError = "Incorrect password.";
        }
      } catch (err) {
        console.error(err);
        this.passError = "Error verifying password.";
      }
    }
  }
};
